// kontaktRollen.ts — die Rollen unter „Sonstige" in der Kontaktanlage, nach
// Bereichen (User 26.09.2026). Ersetzt für die Anlage die beiden Listen aus
// `statusAuswahlKatalog.ts` (Person/Firma getrennt) durch EINE Liste, in der
// jede Rolle weiß, ob sie zu einer Person, einer Firma oder zu beidem passt —
// daraus leitet die Anlage „Firma | Privat" ab.
//
// Die `value`s sind Speicherwerte und müssen zeichengenau zu
// `apps/api/src/contactStatus.katalog.ts` passen — sonst verwirft der Server
// den Status ohne Meldung.
//
// Rollen, die hier fehlen (externe Zuständigkeiten wie Steuerberater,
// Schadenverursacher, Regulierer VR …), sind in der Anlage nicht mehr wählbar.
// Bestehende Kontakte behalten sie; die Akte zeigt sie weiter an.

import type { BezugsArt, StatusEintrag } from "./statusAuswahlKatalog";
import { kanonischerStatus } from "./sonstigeStatusKatalog";

/** Zu welchem Kontakttyp eine Rolle passt. */
export type RollenTyp = "person" | "firma" | "beide";

/**
 * Welche Felder direkt unter der Rollenwahl erscheinen (nicht als Popup):
 *  - "unternehmen": Unternehmen + Verantwortlichkeit (wie „Verbindungen zu Unternehmen")
 *  - "verein": dasselbe mit einem Verein als Gegenüber
 *  - "person": „+ Person mit Beziehung" mit Haken „lebt im selben Haushalt"
 * Die Einträge landen in denselben Listen wie die Karten unten.
 */
export type RollenFolge = "unternehmen" | "verein" | "person";

export type KontaktRolle = {
  value: string;
  label: string;
  typ: RollenTyp;
  /** Gegenüber, nach dem gefragt wird („… — für wen?"). Nicht bei `folge`. */
  bezug?: BezugsArt;
  folge?: RollenFolge;
  /** Nur mit dem Recht `cointern:read` wählbar (Courtés eigene Geschäftsbeziehung). */
  nurCoIntern?: boolean;
};

export type RollenBereich = { titel: string; rollen: KontaktRolle[] };

/** Eingebauter Startbestand und Fallback (siehe „Aktiver Katalog" unten). */
export const ROLLEN_BEREICHE: RollenBereich[] = [
  {
    titel: "Frühere Geschäftsbeziehung",
    rollen: [
      { value: "Altkunde", label: "Altkunde", typ: "beide" },
      { value: "Altinteressent", label: "Altinteressent", typ: "beide" },
    ],
  },
  {
    titel: "Vertrieb und Netzwerk",
    rollen: [
      { value: "Vermittler, Tippgeber", label: "Vermittler/Tippgeber", typ: "beide" },
      { value: "Empfehlungsgeber", label: "Empfehlungsgeber", typ: "beide" },
      { value: "Sponsor/Spender", label: "Sponsor/Spender", typ: "beide" },
      { value: "Ansprechpartner Produktpartner", label: "Ansprechpartner eines Produktpartners", typ: "person", bezug: "firma" },
      { value: "Ansprechpartner eines Lieferanten", label: "Ansprechpartner eines Lieferanten", typ: "person", bezug: "firma" },
      { value: "Produktpartner", label: "Produktpartner", typ: "firma" },
      { value: "Kooperationspartner", label: "Kooperationspartner", typ: "firma" },
      { value: "Ansprechpartner Kooperationspartner", label: "Ansprechpartner Kooperationspartner", typ: "person", bezug: "firma" },
      { value: "Lieferant", label: "Lieferant", typ: "firma", nurCoIntern: true },
    ],
  },
  {
    titel: "Beziehung zu Privatkontakten",
    rollen: [
      { value: "Haushaltsmitglied", label: "im selben Haushalt", typ: "person", folge: "person" },
      { value: "Kontakt mit Beziehung zu einem Kontakt", label: "außerhalb des Haushalts", typ: "person", folge: "person" },
    ],
  },
  {
    titel: "Verbindung zu Unternehmen",
    rollen: [
      { value: "Mensch im Unternehmen", label: "Mensch im Unternehmen", typ: "person", folge: "unternehmen" },
      // Speicherwert bis 29.09.2026 „Mensch fürs Unternehmen" (Migration 202609290070).
      { value: "Verantwortung fürs Unternehmen", label: "Verantwortung fürs Unternehmen", typ: "person", folge: "unternehmen" },
      { value: "Arbeitgeber eines Privat-Kontaktes", label: "Arbeitgeber", typ: "firma", bezug: "person" },
    ],
  },
  {
    titel: "Verbindung zu Verein, Stiftung",
    rollen: [
      { value: "Mitgliedschaft in einem Verein", label: "Mitgliedschaft in einem Verein", typ: "beide", folge: "verein" },
      { value: "Verantwortung für einen Verein", label: "Verantwortung für einen Verein", typ: "person", folge: "verein" },
    ],
  },
  {
    titel: "Beteiligungen (direkt / indirekt)",
    rollen: [
      { value: "Muttergesellschaft von", label: "Muttergesellschaft", typ: "firma", bezug: "firma" },
      { value: "Tochtergesellschaft von", label: "Tochtergesellschaft", typ: "firma", bezug: "firma" },
      { value: "Schwestergesellschaft mit Beteiligung", label: "Schwestergesellschaft mit Beteiligung", typ: "firma", bezug: "firma" },
      { value: "Schwestergesellschaft ohne Beteiligung", label: "Schwestergesellschaft ohne Beteiligung", typ: "firma", bezug: "firma" },
      { value: "Joint Venture mit", label: "Joint Venture", typ: "firma", bezug: "firma" },
      { value: "Beteiligung außerhalb des Firmenverbundes an", label: "außerhalb des Firmenverbundes", typ: "firma", bezug: "firma" },
    ],
  },
  {
    titel: "Rolle an Immobilie",
    rollen: [
      "Eigentümer",
      "Verwalter",
      "Mieter",
      "Vermieter",
      "Pächter",
      "Verpächter",
      "Erbbauberechtigter",
      "Erbbaurechtsgeber",
      "Nießbrauchberechtigter",
      "Nießbrauchgeber",
      "Zwangsverwalter",
    ].map((v) => ({ value: v, label: v, typ: "beide" as const, bezug: "kontakt" as const })),
  },
  {
    titel: "Rolle an sonstigen Risiken",
    rollen: [
      { value: "Betreiber", label: "Betreiber", typ: "beide", bezug: "kontakt" },
      { value: "Nutzer", label: "Nutzer", typ: "beide", bezug: "kontakt" },
      { value: "Leasinggeber", label: "Leasinggeber", typ: "beide", bezug: "kontakt" },
      { value: "Bank/Finanzierer", label: "Bank", typ: "firma", bezug: "kontakt" },
      { value: "Leasingnehmer", label: "Leasingnehmer", typ: "beide", bezug: "kontakt" },
      { value: "Halter", label: "Halter", typ: "beide" },
    ],
  },
  {
    titel: "Rolle am Schaden",
    rollen: [
      { value: "Anspruchsteller", label: "Anspruchsteller", typ: "beide" },
      { value: "Geschädigter", label: "Geschädigter", typ: "beide" },
      { value: "Sachverständiger", label: "Sachverständiger", typ: "beide", bezug: "kontakt" },
      { value: "Reparaturfirma", label: "Reparaturfirma", typ: "firma", bezug: "kontakt" },
      { value: "Rechtsanwalt im Schaden", label: "Rechtsanwalt", typ: "beide", bezug: "kontakt" },
    ],
  },
];

// ── Aktiver Katalog ──────────────────────────────────────────────────────
//
// Seit 27.09.2026 pflegt der Admin den Katalog in Einstellungen →
// Kontaktrollen (`GET /api/kontakt-rollen`, je Mandant). `ROLLEN_BEREICHE`
// oben ist der eingebaute Startbestand und der Fallback, solange der Server
// nichts (oder nichts Brauchbares) liefert — etwa lokal, wenn Vite `/api` an
// einen DEV-Server ohne den Endpoint weiterreicht. Geladen wird einmal über
// `useKontaktRollen` (services/useKontaktRollen.ts), das hier den Modul-State
// setzt; alle Funktionen unten lesen ihn.

let katalog: RollenBereich[] = ROLLEN_BEREICHE;
let alle = flach(katalog);

/**
 * Alle Rollen flach, mit Bereich. Seit 29.09.2026 darf eine Rolle (gleicher
 * `value` = gleicher Status am Kontakt) in mehreren Bereichen stehen:
 * `rolleZu` liefert dann den ersten Bereich, und bei verschiedenem Typ gilt
 * „beide" — wie im Backend (`kontaktRollenTyp.ts → typZusammenfuehren`).
 */
function flach(bereiche: RollenBereich[]) {
  const liste = bereiche.flatMap((b) => b.rollen.map((r) => ({ ...r, bereich: b.titel })));
  const typ = new Map<string, RollenTyp>();
  for (const r of liste) {
    const bisher = typ.get(r.value);
    typ.set(r.value, bisher === undefined || bisher === r.typ ? r.typ : "beide");
  }
  return liste.map((r) => (typ.get(r.value) === r.typ ? r : { ...r, typ: typ.get(r.value) as RollenTyp }));
}

/** Alle Bereiche, in denen eine Rolle steht (Katalogreihenfolge). */
export const bereicheVon = (value: string): string[] => {
  const v = kanonischerStatus(value);
  return alle.filter((r) => r.value === v).map((r) => r.bereich);
};

/** Der Katalog, mit dem die Anlage gerade arbeitet. */
export const aktuellerRollenKatalog = (): RollenBereich[] => katalog;

/** Katalog setzen — `null` oder leer stellt den eingebauten wieder her. */
export function setzeRollenKatalog(bereiche: RollenBereich[] | null | undefined): void {
  katalog = bereiche && bereiche.length ? bereiche : ROLLEN_BEREICHE;
  alle = flach(katalog);
}

const TYPEN: RollenTyp[] = ["person", "firma", "beide"];
const BEZUEGE: BezugsArt[] = ["firma", "person", "kontakt"];
const FOLGEN: RollenFolge[] = ["unternehmen", "verein", "person"];

/**
 * Antwort von `GET /api/kontakt-rollen` → Katalog der Anlage. Deaktivierte
 * Bereiche und Rollen fallen heraus (sie sind dann „Altbestand": an
 * bestehenden Kontakten sichtbar, aber nicht mehr wählbar), leere Bereiche
 * ebenso. Liefert `null`, wenn die Antwort nicht die erwartete Form hat —
 * dann bleibt der eingebaute Katalog.
 */
export function katalogAusServer(roh: unknown): RollenBereich[] | null {
  const bereiche = (roh as { bereiche?: unknown } | null)?.bereiche;
  if (!Array.isArray(bereiche)) return null;
  const out: RollenBereich[] = [];
  for (const b of bereiche) {
    if (!b || typeof b !== "object" || typeof b.titel !== "string" || !Array.isArray(b.rollen)) return null;
    if (b.aktiv === false) continue;
    const rollen: KontaktRolle[] = [];
    for (const r of b.rollen) {
      if (!r || typeof r.value !== "string" || !r.value) return null;
      if (r.aktiv === false) continue;
      const typ: RollenTyp = TYPEN.includes(r.typ) ? r.typ : "beide";
      rollen.push({
        value: r.value,
        label: typeof r.label === "string" && r.label.trim() ? r.label : r.value,
        typ,
        ...(BEZUEGE.includes(r.bezug) && !FOLGEN.includes(r.folge) ? { bezug: r.bezug as BezugsArt } : {}),
        ...(FOLGEN.includes(r.folge) ? { folge: r.folge as RollenFolge } : {}),
        ...(r.nurCoIntern === true ? { nurCoIntern: true } : {}),
      });
    }
    if (rollen.length) out.push({ titel: b.titel, rollen });
  }
  return out.length ? out : null;
}

/** Die Bereiche, die dieser Anwender sieht (CO-intern nur mit Recht). */
export const sichtbareBereiche = (coIntern: boolean): RollenBereich[] =>
  katalog
    .map((b) => ({ ...b, rollen: b.rollen.filter((r) => coIntern || !r.nurCoIntern) }))
    .filter((b) => b.rollen.length > 0);

export const rolleZu = (value: string): (KontaktRolle & { bereich: string }) | undefined => {
  // Alter Speicherwert („Mensch fürs Unternehmen", bis 29.09.2026) → heutiger.
  const v = kanonischerStatus(value);
  return alle.find((r) => r.value === v);
};

/** Bereich einer Rolle — für eine Rolle aus dem Altbestand `undefined`. */
export const bereichVon = (value: string): string | undefined => rolleZu(value)?.bereich;

/**
 * Welcher Typ aus den gewählten Rollen folgt:
 *  - "person"/"firma": mindestens eine Rolle legt ihn fest
 *  - null: alle Rollen passen zu beidem (oder keine gewählt) → Anwender wählt
 *  - "konflikt": Person- UND Firmenrollen gemischt (darf die Auswahl nicht zulassen)
 */
export const typAusRollen = (rollen: string[]): "person" | "firma" | null | "konflikt" => {
  const typen = new Set(rollen.map((r) => rolleZu(r)?.typ).filter((t) => t && t !== "beide"));
  if (typen.size > 1) return "konflikt";
  return (([...typen][0] as "person" | "firma" | undefined) ?? null);
};

/** Passt die Rolle zum festgelegten Typ? (Ohne Typ passt jede.) */
export const passtZuTyp = (value: string, typ: "person" | "firma" | null): boolean => {
  const t = rolleZu(value)?.typ;
  return !typ || !t || t === "beide" || t === typ;
};

/** Welche Folgefelder die gewählten Rollen öffnen — ohne Dubletten. */
export const folgenAus = (rollen: string[]): RollenFolge[] => [
  ...new Set(rollen.map((r) => rolleZu(r)?.folge).filter((f): f is RollenFolge => !!f)),
];

/**
 * Derselbe Katalog in der Form, die `StatusBezuege` liest: Bereich = Überschrift,
 * Rollen = Details. Rollen mit Folgefeldern bekommen KEIN Gegenüber — ihr Bezug
 * steht in den Folgefeldern (Haushalt, Unternehmen, Verein).
 */
export const alsStatusEintraege = (): StatusEintrag[] =>
  katalog.map((b) => ({
    label: b.titel,
    details: b.rollen.map((r) => ({ value: r.value, label: r.label, ...(r.bezug ? { bezug: r.bezug } : {}) })),
  }));
